import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast, Toaster } from "sonner";
import { 
  ClipboardCheck, Building2, MapPin, Calendar, CheckCircle2, 
  AlertTriangle, Clock, Star, Upload, X, Loader2, 
  ChevronLeft, ChevronRight, Eye, TrendingUp
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// تصنيفات التقييم (الأجهزة الطبية والكهربائية تُبنى ديناميكياً من الأجهزة الفعلية)
const EVALUATION_CATEGORIES = [
  {
    id: "infrastructure",
    name: "الأعمال الإنشائية",
    icon: "🏗️",
    items: [
      { id: "walls", name: "الجدران والدهانات" },
      { id: "floors", name: "الأرضيات" },
      { id: "ceiling", name: "السقف" },
      { id: "doors", name: "الأبواب والنوافذ" },
      { id: "ac", name: "التكييف" },
      { id: "plumbing", name: "السباكة" }
    ]
  },
  {
    id: "cleanliness",
    name: "النظافة",
    icon: "🧹",
    items: [
      { id: "general_clean", name: "النظافة العامة" },
      { id: "bathroom_clean", name: "نظافة دورات المياه" },
      { id: "waste", name: "إدارة النفايات" },
      { id: "odor", name: "الروائح" }
    ]
  },
  {
    id: "safety",
    name: "السلامة",
    icon: "🛡️",
    items: [
      { id: "fire_extinguisher", name: "طفايات الحريق" },
      { id: "emergency_exit", name: "مخارج الطوارئ" },
      { id: "electrical_safety", name: "السلامة الكهربائية" },
      { id: "first_aid", name: "صندوق الإسعافات" },
      { id: "signage", name: "اللوحات الإرشادية" }
    ]
  },
  {
    id: "it",
    name: "تقنية المعلومات",
    icon: "💻",
    items: [
      { id: "computers", name: "أجهزة الكمبيوتر" },
      { id: "network", name: "الشبكة والإنترنت" },
      { id: "printers", name: "الطابعات" },
      { id: "phones", name: "الهواتف" }
    ]
  }
];

const DEVICE_CATEGORY_META = {
  medical_devices: { name: "الأجهزة الطبية", icon: "🏥" },
  electrical_devices: { name: "الأجهزة الإلكترونية", icon: "⚡" },
  electric_devices: { name: "الأجهزة الكهربائية", icon: "🔌" },
  // legacy id from older evaluations
  medical_equipment: { name: "المعدات الطبية", icon: "🏥" },
};

const SCORE_OPTIONS = [
  { value: 4, label: "ممتاز", color: "bg-green-500", emoji: "😊" },
  { value: 3, label: "جيد", color: "bg-blue-500", emoji: "🙂" },
  { value: 2, label: "متوسط", color: "bg-yellow-500", emoji: "😐" },
  { value: 1, label: "سيء", color: "bg-red-500", emoji: "😞" }
];

// حالات تقييم الأجهزة — موحدة مع تقييم الأجهزة في صفحة الأجهزة
const DEVICE_STATUS_OPTIONS = [
  { value: "operational", label: "يعمل بكفاءة", color: "bg-green-500", score: 4 },
  { value: "needs_maintenance", label: "يحتاج صيانة", color: "bg-yellow-500", score: 2 },
  { value: "out_of_service", label: "خارج الخدمة", color: "bg-red-500", score: 1 }
];

const isDeviceCategory = (catId) => catId === "medical_devices" || catId === "electrical_devices" || catId === "electric_devices";

const formatPeriod = (p) => {
  if (!p) return p;
  if (p.startsWith("H1-")) return `النصف الأول ${p.slice(3)}`;
  if (p.startsWith("H2-")) return `النصف الثاني ${p.slice(3)}`;
  return p;
};

let EVAL_CATEGORIES = [...EVALUATION_CATEGORIES];

// Get categories based on location type
const getCategoriesForLocation = (locationType) => {
  if (locationType === "restroom") return EVAL_CATEGORIES.filter(c => c.id !== "it");
  return EVAL_CATEGORIES;
};

const Evaluations = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("schedule");
  const [schedule, setSchedule] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [evaluationDialogOpen, setEvaluationDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState({});
  const [overallNotes, setOverallNotes] = useState("");
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [locationTypeFilter, setLocationTypeFilter] = useState("all");
  const [locationDevices, setLocationDevices] = useState([]);
  const [criteria, setCriteria] = useState(EVALUATION_CATEGORIES);
  const [newCatName, setNewCatName] = useState("");
  const [newItemNames, setNewItemNames] = useState({});
  const isAdmin = user?.role === "admin";

  const loadCriteria = async () => {
    try {
      const res = await axios.get(`${API}/evaluation-criteria`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.length) {
        EVAL_CATEGORIES = res.data;
        setCriteria(res.data);
      }
    } catch { /* fallback to static */ }
  };
  useEffect(() => { loadCriteria(); }, [token]);

  const addCriteriaCategory = async () => {
    if (!newCatName.trim()) { toast.error("اسم التصنيف مطلوب"); return; }
    try {
      await axios.post(`${API}/evaluation-criteria`, { name: newCatName }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("تمت إضافة التصنيف");
      setNewCatName("");
      loadCriteria();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل في الإضافة"); }
  };

  const deleteCriteriaCategory = async (cat) => {
    if (!window.confirm(`حذف تصنيف "${cat.name}" وجميع بنوده؟ (التقييمات السابقة لن تتأثر)`)) return;
    try {
      await axios.delete(`${API}/evaluation-criteria/${cat.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("تم حذف التصنيف");
      loadCriteria();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل في الحذف"); }
  };

  const addCriteriaItem = async (cat) => {
    const name = (newItemNames[cat.id] || "").trim();
    if (!name) { toast.error("اسم البند مطلوب"); return; }
    try {
      await axios.post(`${API}/evaluation-criteria/${cat.id}/items`, { name }, { headers: { Authorization: `Bearer ${token}` } });
      setNewItemNames({ ...newItemNames, [cat.id]: "" });
      loadCriteria();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل في الإضافة"); }
  };

  const deleteCriteriaItem = async (cat, item) => {
    if (!window.confirm(`حذف بند "${item.name}"؟`)) return;
    try {
      await axios.delete(`${API}/evaluation-criteria/${cat.id}/items/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
      loadCriteria();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل في الحذف"); }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [scheduleRes, evaluationsRes, statsRes] = await Promise.all([
        axios.get(`${API}/evaluations/schedule`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/evaluations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/evaluations/stats/summary`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setSchedule(scheduleRes.data);
      setEvaluations(evaluationsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("فشل في تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  const startEvaluation = async (location) => {
    setSelectedLocation(location);
    setCurrentCategoryIndex(0);
    setScores({});
    setNotes({});
    setOverallNotes("");
    setImages([]);
    setLocationDevices([]);
    // Load the real medical & electrical devices of this location
    if (location?.location_type && location.location_type !== "restroom") {
      try {
        const res = await axios.get(
          `${API}/biomed-devices?location_type=${location.location_type}&location_id=${location.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setLocationDevices((res.data || []).filter(d => d.device_type === "biomed" || d.device_type === "electrical" || d.device_type === "electric"));
      } catch {
        setLocationDevices([]);
      }
    }
    setEvaluationDialogOpen(true);
  };

  const handleScoreChange = (categoryId, itemId, score) => {
    setScores(prev => ({
      ...prev,
      [`${categoryId}_${itemId}`]: score
    }));
  };

  const handleNoteChange = (categoryId, itemId, note) => {
    setNotes(prev => ({
      ...prev,
      [`${categoryId}_${itemId}`]: note
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 10) {
      toast.error("الحد الأقصى 10 صور");
      return;
    }

    for (const file of files) {
      const reader = new FileReader();
      reader.onload = () => {
        setImages(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const baseCategories = selectedLocation ? getCategoriesForLocation(selectedLocation.location_type) : EVALUATION_CATEGORIES;
  const deviceToItem = (d) => ({
    id: d.id,
    name: `${d.device_name} — ${d.biomed_no}`,
    device_id: d.id,
    locked: d.can_evaluate_now === false,
    next_available: d.next_evaluation_available_at,
    latest_status: d.latest_status,
  });
  const biomedItems = locationDevices.filter(d => d.device_type === "biomed").map(deviceToItem);
  const electricalItems = locationDevices.filter(d => d.device_type === "electrical").map(deviceToItem);
  const electricItems = locationDevices.filter(d => d.device_type === "electric").map(deviceToItem);
  const deviceCategories = [];
  if (selectedLocation?.location_type === "clinic" || biomedItems.length > 0) {
    deviceCategories.push({
      id: "medical_devices",
      name: "الأجهزة الطبية",
      icon: "🏥",
      items: biomedItems.length > 0 ? biomedItems : [{ id: "no_devices", name: "لا توجد أجهزة طبية مسجّلة لهذا الموقع — أضفها من صفحة الأجهزة أولاً" }],
    });
  }
  if (electricalItems.length > 0) {
    deviceCategories.push({ id: "electrical_devices", name: "الأجهزة الإلكترونية", icon: "⚡", items: electricalItems });
  }
  if (electricItems.length > 0) {
    deviceCategories.push({ id: "electric_devices", name: "الأجهزة الكهربائية", icon: "🔌", items: electricItems });
  }
  const activeCategories = [...deviceCategories, ...baseCategories];
  const currentCategory = activeCategories[currentCategoryIndex];

  const itemDone = (catId, item) => item.id === "no_devices" || item.locked || !!scores[`${catId}_${item.id}`];

  const getCategoryProgress = (categoryId) => {
    const category = activeCategories.find(c => c.id === categoryId);
    if (!category) return 0;
    const scoredItems = category.items.filter(item => itemDone(categoryId, item));
    return (scoredItems.length / category.items.length) * 100;
  };

  const isCurrentCategoryComplete = () => {
    if (!currentCategory) return false;
    return currentCategory.items.every(item => itemDone(currentCategory.id, item));
  };

  const submitEvaluation = async () => {
    // Check all categories are complete
    const allComplete = activeCategories.every(cat =>
      cat.items.every(item => itemDone(cat.id, item))
    );

    if (!allComplete) {
      toast.error("يرجى تقييم جميع العناصر");
      return;
    }

    setSubmitting(true);
    try {
      const categories = activeCategories
        .map(cat => {
          const isDev = isDeviceCategory(cat.id);
          const items = cat.items
            .filter(item => item.id !== "no_devices" && !item.locked)
            .map(item => {
              if (isDev) {
                const status = scores[`${cat.id}_${item.id}`];
                const opt = DEVICE_STATUS_OPTIONS.find(o => o.value === status);
                return {
                  item_name: item.name,
                  device_id: item.device_id,
                  status,
                  score: opt?.score || 0,
                  notes: notes[`${cat.id}_${item.id}`] || "",
                  has_issue: status !== "operational",
                };
              }
              return {
                item_name: item.name,
                score: scores[`${cat.id}_${item.id}`],
                notes: notes[`${cat.id}_${item.id}`] || "",
                has_issue: scores[`${cat.id}_${item.id}`] <= 2,
              };
            });
          return { category: cat.id, items };
        })
        .filter(cat => cat.items.length > 0);

      const res = await axios.post(`${API}/evaluations`, {
        location_id: selectedLocation.id,
        location_type: selectedLocation.location_type,
        categories,
        overall_notes: overallNotes,
        images
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const synced = res.data?.devices_synced || 0;
      toast.success(synced > 0 ? `تم حفظ التقييم — وتحديث سجل ${synced} جهاز` : "تم حفظ التقييم بنجاح");
      setEvaluationDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("فشل في حفظ التقييم");
    } finally {
      setSubmitting(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return "text-green-600";
    if (score >= 70) return "text-blue-600";
    if (score >= 50) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBadge = (score) => {
    if (score >= 90) return "bg-green-100 text-green-700";
    if (score >= 70) return "bg-blue-100 text-blue-700";
    if (score >= 50) return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  const viewEvaluation = (evaluation) => {
    setSelectedEvaluation(evaluation);
    setViewDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#4a3fa8]">التقييم الدوري</h1>
          <p className="text-slate-500">تقييم حالة العيادات والمرافق</p>
        </div>
        {stats && (
          <Badge className="bg-[#655dc6] text-white text-lg px-4 py-2 hover:bg-[#655dc6] cursor-default" data-testid="quarter-badge">
            {formatPeriod(stats.current_quarter)}
          </Badge>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-[#655dc6]">{stats.total_locations}</p>
              <p className="text-sm text-slate-500">إجمالي المواقع</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-green-50">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.evaluated_count}</p>
              <p className="text-sm text-green-700">تم تقييمها</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-yellow-50">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{stats.pending_count}</p>
              <p className="text-sm text-yellow-700">بانتظار التقييم</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-blue-50">
            <CardContent className="pt-4 text-center">
              <p className={`text-2xl font-bold ${getScoreColor(stats.average_score)}`}>
                {stats.average_score}%
              </p>
              <p className="text-sm text-blue-700">متوسط التقييم</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} dir="rtl">
        <TabsList className={`grid w-full ${isAdmin ? "grid-cols-3" : "grid-cols-2"}`}>
          <TabsTrigger value="schedule" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            جدول التقييم
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            التقييمات السابقة
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="criteria" className="flex items-center gap-2" data-testid="tab-criteria">
              <Star className="w-4 h-4" />
              بنود التقييم
            </TabsTrigger>
          )}
        </TabsList>

        {/* Criteria Management Tab (admin) */}
        {isAdmin && (
          <TabsContent value="criteria" className="mt-4 space-y-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-5">
                <p className="text-sm text-slate-500 mb-3">إدارة تصنيفات وبنود التقييم الدوري — أي تعديل يظهر فوراً في نموذج التقييم دون كود. (تصنيفات الأجهزة تُبنى تلقائياً من الأجهزة المسجلة)</p>
                <div className="flex gap-2 max-w-md">
                  <Textarea rows={1} value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="اسم تصنيف جديد..." className="resize-none min-h-[40px]" data-testid="new-cat-name" />
                  <Button onClick={addCriteriaCategory} className="bg-[#655dc6] hover:bg-[#5449a8] text-white shrink-0" data-testid="add-cat-btn">إضافة تصنيف</Button>
                </div>
              </CardContent>
            </Card>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {criteria.map(cat => (
                <Card key={cat.id} className="border-0 shadow-sm" data-testid={`crit-cat-${cat.id}`}>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-base text-[#655dc6]">{cat.icon} {cat.name} ({cat.items.length})</CardTitle>
                    <Button size="sm" variant="ghost" onClick={() => deleteCriteriaCategory(cat)} className="text-red-400 h-7 w-7 p-0" data-testid={`del-cat-${cat.id}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-1.5">
                    {cat.items.map(it => (
                      <div key={it.id} className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-1.5" data-testid={`crit-item-${it.id}`}>
                        <span className="text-sm">{it.name}</span>
                        <Button size="sm" variant="ghost" onClick={() => deleteCriteriaItem(cat, it)} className="text-red-300 hover:text-red-500 h-6 w-6 p-0" data-testid={`del-item-${it.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2 pt-1">
                      <Textarea rows={1} value={newItemNames[cat.id] || ""} onChange={e => setNewItemNames({ ...newItemNames, [cat.id]: e.target.value })} placeholder="بند جديد..." className="resize-none min-h-[36px] text-sm" data-testid={`new-item-${cat.id}`} />
                      <Button size="sm" onClick={() => addCriteriaItem(cat)} variant="outline" className="shrink-0 border-[#655dc6] text-[#655dc6]" data-testid={`add-item-${cat.id}`}>إضافة</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}

        {/* Schedule Tab */}
        <TabsContent value="schedule" className="mt-4">
          {/* Location Type Filter Tabs */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="location-type-filters">
            {[
              { value: "all", label: "الكل", count: schedule.length },
              { value: "clinic", label: "العيادات", count: schedule.filter(l => l.location_type === "clinic").length },
              { value: "office", label: "المكاتب", count: schedule.filter(l => l.location_type === "office").length },
              { value: "restroom", label: "دورات المياه", count: schedule.filter(l => l.location_type === "restroom").length },
              { value: "facility", label: "المرافق", count: schedule.filter(l => l.location_type === "facility").length },
            ].map((type) => (
              <button
                key={type.value}
                onClick={() => setLocationTypeFilter(type.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  locationTypeFilter === type.value
                    ? "bg-[#655dc6] text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid={`filter-${type.value}`}
              >
                <span>{type.label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  locationTypeFilter === type.value
                    ? "bg-white/20"
                    : "bg-slate-200"
                }`}>{type.count}</span>
              </button>
            ))}
          </div>

          <div className="grid gap-4">
            {schedule.filter(loc => !loc.evaluated && (locationTypeFilter === "all" || loc.location_type === locationTypeFilter)).length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
                  <h3 className="text-lg font-semibold text-green-600 mb-2">تم تقييم جميع المواقع!</h3>
                  <p className="text-slate-500">جميع المواقع تم تقييمها لهذه الفترة (كل 6 أشهر)</p>
                </CardContent>
              </Card>
            ) : (
              schedule.filter(loc => !loc.evaluated && (locationTypeFilter === "all" || loc.location_type === locationTypeFilter)).map((location) => (
                <Card key={location.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{location.name}</h3>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {location.floor} {location.building && `• ${location.building}`}
                            </span>
                            <Badge variant="outline">{location.location_type === "clinic" ? "عيادة" : location.location_type === "office" ? "مكتب" : location.location_type === "facility" ? "مرفق" : "دورة مياه"}</Badge>
                          </div>
                        </div>
                      </div>
                      <Button 
                        onClick={() => startEvaluation(location)}
                        className="bg-[#4a3fa8] hover:bg-[#3d3291] text-white"
                        data-testid={`start-evaluation-${location.id}`}
                      >
                        <ClipboardCheck className="w-4 h-4 ml-2" />
                        بدء التقييم
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}

            {/* Already Evaluated */}
            {schedule.filter(loc => loc.evaluated && (locationTypeFilter === "all" || loc.location_type === locationTypeFilter)).length > 0 && (
              <>
                <h3 className="text-lg font-semibold text-slate-600 mt-4">تم تقييمها</h3>
                {schedule.filter(loc => loc.evaluated && (locationTypeFilter === "all" || loc.location_type === locationTypeFilter)).map((location) => (
                  <Card key={location.id} className="border-0 shadow-sm bg-green-50/50">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                            <CheckCircle2 className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{location.name}</h3>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              <span>تم التقييم: {new Date(location.evaluation_date).toLocaleDateString("ar-SA")}</span>
                              <Badge className={getScoreBadge(location.overall_score)}>
                                {location.overall_score}%
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          {evaluations.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <ClipboardCheck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-500 mb-2">لا توجد تقييمات</h3>
                <p className="text-slate-400">لم يتم إجراء أي تقييمات بعد</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {evaluations.map((evaluation) => (
                <Card 
                  key={evaluation.id} 
                  className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => viewEvaluation(evaluation)}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{evaluation.location_name}</h3>
                          <Badge className={getScoreBadge(evaluation.overall_score)}>
                            {evaluation.overall_score}%
                          </Badge>
                          {evaluation.issues_found > 0 && (
                            <Badge className="bg-red-100 text-red-700">
                              <AlertTriangle className="w-3 h-3 ml-1" />
                              {evaluation.issues_found} مشكلة
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(evaluation.created_at).toLocaleDateString("ar-SA")}
                          </span>
                          <span>المُقيّم: {evaluation.evaluator_name}</span>
                          <Badge variant="outline">{formatPeriod(evaluation.quarter)}</Badge>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        <Eye className="w-4 h-4 ml-1" />
                        عرض
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Evaluation Dialog */}
      <Dialog open={evaluationDialogOpen} onOpenChange={setEvaluationDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#4a3fa8]">
              <div className="flex items-center justify-between pl-8">
                <span>تقييم: {selectedLocation?.name}</span>
                <Badge className="bg-[#655dc6] text-white">
                  {currentCategoryIndex + 1} / {activeCategories.length}
                </Badge>
              </div>
            </DialogTitle>
          </DialogHeader>

          {currentCategory && (
            <div className="space-y-6">
              {/* Category Header */}
              <div className="flex items-center gap-3 p-4 bg-[#F5F3F9] rounded-lg">
                <span className="text-3xl">{currentCategory.icon}</span>
                <div>
                  <h3 className="font-bold text-lg text-[#4a3fa8]">{currentCategory.name}</h3>
                  <p className="text-sm text-slate-500">
                    {currentCategory.items.filter(item => itemDone(currentCategory.id, item)).length} / {currentCategory.items.length} عنصر
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-[#655dc6] h-2 rounded-full transition-all"
                  style={{ width: `${getCategoryProgress(currentCategory.id)}%` }}
                />
              </div>

              {/* Items */}
              <div className="space-y-4">
                {currentCategory.items.map((item) => {
                  const isPlaceholder = item.id === "no_devices";
                  const isDevCat = isDeviceCategory(currentCategory.id);
                  const key = `${currentCategory.id}_${item.id}`;
                  if (isDevCat && item.locked) {
                    const latestOpt = DEVICE_STATUS_OPTIONS.find(o => o.value === item.latest_status);
                    return (
                      <div key={item.id} className="p-4 border rounded-lg bg-slate-50 border-slate-200" data-testid={`eval-locked-${item.id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <span className="font-medium text-slate-500">{item.name}</span>
                          <div className="flex items-center gap-2">
                            {latestOpt && <Badge className={latestOpt.color}>{latestOpt.label}</Badge>}
                            <Badge variant="outline" className="text-slate-500">
                              <Clock className="w-3 h-3 ml-1" />
                              تم تقييمه خلال آخر 6 أشهر
                            </Badge>
                          </div>
                        </div>
                        {item.next_available && (
                          <p className="text-xs text-slate-400 mt-2">التقييم القادم متاح بتاريخ: {new Date(item.next_available).toLocaleDateString("ar-SA")}</p>
                        )}
                      </div>
                    );
                  }
                  return (
                  <div key={item.id} className={`p-4 border rounded-lg ${isPlaceholder ? "bg-amber-50 border-amber-200" : ""}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium">{item.name}</span>
                      {!isPlaceholder && scores[key] && (
                        <Badge className={(isDevCat ? DEVICE_STATUS_OPTIONS.find(s => s.value === scores[key]) : SCORE_OPTIONS.find(s => s.value === scores[key]))?.color}>
                          {(isDevCat ? DEVICE_STATUS_OPTIONS.find(s => s.value === scores[key]) : SCORE_OPTIONS.find(s => s.value === scores[key]))?.label}
                        </Badge>
                      )}
                    </div>
                    {!isPlaceholder && (
                    <>
                    {/* Score / Status Buttons */}
                    {isDevCat ? (
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {DEVICE_STATUS_OPTIONS.map((option) => (
                          <Button
                            key={option.value}
                            variant={scores[key] === option.value ? "default" : "outline"}
                            className={scores[key] === option.value ? option.color : ""}
                            onClick={() => handleScoreChange(currentCategory.id, item.id, option.value)}
                            data-testid={`eval-status-${item.id}-${option.value}`}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    ) : (
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {SCORE_OPTIONS.map((option) => (
                        <Button
                          key={option.value}
                          variant={scores[key] === option.value ? "default" : "outline"}
                          className={scores[key] === option.value ? option.color : ""}
                          onClick={() => handleScoreChange(currentCategory.id, item.id, option.value)}
                        >
                          <span className="ml-1">{option.emoji}</span>
                          {option.label}
                        </Button>
                      ))}
                    </div>
                    )}

                    {/* Notes for this item */}
                    {scores[key] && (isDevCat ? scores[key] !== "operational" : scores[key] <= 2) && (
                      <Textarea
                        placeholder="أضف ملاحظة عن المشكلة..."
                        value={notes[key] || ""}
                        onChange={(e) => handleNoteChange(currentCategory.id, item.id, e.target.value)}
                        className="text-right"
                      />
                    )}
                    </>
                    )}
                  </div>
                  );
                })}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setCurrentCategoryIndex(prev => prev - 1)}
                  disabled={currentCategoryIndex === 0}
                >
                  <ChevronRight className="w-4 h-4 ml-1" />
                  السابق
                </Button>

                {currentCategoryIndex < activeCategories.length - 1 ? (
                  <Button
                    onClick={() => setCurrentCategoryIndex(prev => prev + 1)}
                    disabled={!isCurrentCategoryComplete()}
                    className="bg-[#655dc6] hover:bg-[#5449a8] text-white"
                  >
                    التالي
                    <ChevronLeft className="w-4 h-4 mr-1" />
                  </Button>
                ) : (
                  <div className="space-y-4 flex-1 mr-4">
                    {/* Overall Notes */}
                    <div>
                      <Label>ملاحظات عامة</Label>
                      <Textarea
                        value={overallNotes}
                        onChange={(e) => setOverallNotes(e.target.value)}
                        placeholder="أي ملاحظات إضافية..."
                        className="text-right mt-2"
                      />
                    </div>

                    {/* Image Upload */}
                    <div>
                      <Label>صور (اختياري)</Label>
                      <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          id="eval-images"
                        />
                        <label htmlFor="eval-images" className="cursor-pointer">
                          <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                          <p className="text-sm text-slate-500">اضغط لرفع الصور</p>
                        </label>
                      </div>
                      {images.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {images.map((img, index) => (
                            <div key={index} className="relative">
                              <img src={img} alt="" className="w-16 h-16 object-cover rounded" />
                              <button
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full"
                              >
                                <X className="w-3 h-3 mx-auto" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={submitEvaluation}
                      disabled={submitting || !isCurrentCategoryComplete()}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin ml-2" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 ml-2" />
                      )}
                      حفظ التقييم
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Evaluation Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-[#4a3fa8]">
              تفاصيل التقييم: {selectedEvaluation?.location_name}
            </DialogTitle>
          </DialogHeader>

          {selectedEvaluation && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-[#F5F3F9] rounded-lg">
                  <p className={`text-3xl font-bold ${getScoreColor(selectedEvaluation.overall_score)}`}>
                    {selectedEvaluation.overall_score}%
                  </p>
                  <p className="text-sm text-slate-500">النتيجة</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-3xl font-bold text-slate-700">{selectedEvaluation.issues_found}</p>
                  <p className="text-sm text-slate-500">المشاكل</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-bold text-slate-700">{formatPeriod(selectedEvaluation.quarter)}</p>
                  <p className="text-sm text-slate-500">الفترة</p>
                </div>
              </div>

              {/* Categories */}
              {selectedEvaluation.categories?.map((cat, index) => {
                const meta = DEVICE_CATEGORY_META[cat.category] || EVAL_CATEGORIES.find(c => c.id === cat.category);
                return (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">
                      {meta?.icon} {meta?.name || cat.category}
                    </h4>
                    <Badge className={getScoreBadge(cat.average_score * 25)}>
                      {(cat.average_score * 25).toFixed(0)}%
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    {cat.items?.map((item, itemIndex) => {
                      const statusOpt = item.status ? DEVICE_STATUS_OPTIONS.find(s => s.value === item.status) : null;
                      return (
                      <div key={itemIndex} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                        <span>{item.item_name}</span>
                        <div className="flex items-center gap-2">
                          {item.has_issue && <AlertTriangle className="w-4 h-4 text-red-500" />}
                          <Badge className={(statusOpt || SCORE_OPTIONS.find(s => s.value === item.score))?.color || "bg-slate-200"}>
                            {(statusOpt || SCORE_OPTIONS.find(s => s.value === item.score))?.label || item.score}
                          </Badge>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
                );
              })}

              {/* Notes */}
              {selectedEvaluation.overall_notes && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">ملاحظات</p>
                  <p>{selectedEvaluation.overall_notes}</p>
                </div>
              )}

              {/* Images */}
              {selectedEvaluation.images?.length > 0 && (
                <div>
                  <p className="text-sm text-slate-500 mb-2">الصور</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedEvaluation.images.map((img, index) => (
                      <img key={index} src={img} alt="" className="w-24 h-24 object-cover rounded-lg" />
                    ))}
                  </div>
                </div>
              )}

              {/* Meta */}
              <div className="text-sm text-slate-500 pt-4 border-t">
                <p>المُقيّم: {selectedEvaluation.evaluator_name}</p>
                <p>التاريخ: {new Date(selectedEvaluation.created_at).toLocaleString("ar-SA")}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Evaluations;
